import { parseAssetId, SanityImageAssetIdParts } from '@sanity/asset-utils';
import Link from 'next/link';
import Head from 'next/head';
import { GetStaticPaths, GetStaticPropsContext } from 'next';
import PortableText from 'react-portable-text';

import type { Bundle, BlogPostFragment, Product, SanityReference } from 'types';
import type { SanityImageAsset } from '@root/sanity/sanity.types';
import { excerpt, formatPublishedAt } from '@root/pages/wedding-ideas-blog';
import { PAGE_BLOG } from '@root/constants/routes';
import { SEO_TITLE_SUFFIX } from '@root/constants';
import { urlFor } from '@root/lib/sanity';
import IconArrowLeft from 'components/IconArrowLeft';
import BlogPicture from 'components/Blog/Picture';
import NextSteps from 'components/NextSteps';
import Picture from 'components/Picture';
import SalesCalloutInspiration from 'components/SalesCalloutInspiration';
import SalesCalloutSwatches from 'components/SalesCalloutSwatches';
import Card from 'components/Card';
import LayoutBlog from 'layouts/Blog';
import { getBlogPost, getAllBlogPosts } from 'services/blog';
import { withPageStaticProps } from 'utils/header';
import { getItemUrl, getImageUrl as getProductImageUrl } from 'utils/utils';
import { GatedVideoEmbed } from '../../src/components/Video';

type FeaturedProduct = Bundle | Product;

type Props = {
  article: BlogPostFragment;
  error: boolean;
  featuredProducts: FeaturedProduct[];
};

type ButtonStyle = 'default' | 'info' | 'primary' | 'default-outline' | 'info-outline' | 'primary-outline';

type ButtonClass = `btn-${ButtonStyle}`;

const isButtonClass = (value: string): value is ButtonClass => value.includes('btn-');

const buttonStyle = (value: ButtonStyle | ButtonClass | undefined): ButtonClass => {
  if (!value) {
    return 'btn-default';
  }

  if (isButtonClass(value)) {
    return value;
  }

  return `btn-${value}`;
};

const getImageUrl = (asset: SanityImageAsset | SanityReference<SanityImageAsset>): string | null => {
  if (asset) {
    return urlFor(asset).url();
  }

  return null;
};
const escapeDoubleQuotes = (chars: string) => chars.replace(/"/g, `\\"`);

const JsonLdScript = (article: BlogPostFragment, title: string) =>
  `
    <script type="application/ld+json">
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "${escapeDoubleQuotes(title)}",
        "image": "${article.mainImage.asset.url}",
        "datePublished": "${article.publishedAt}",
        "author": "Menguin"
      }
    </script>
  `;

const Blog = ({ article, featuredProducts, error }: Props): JSX.Element => {
  const title = article.title ?? 'Missing Title';
  const pageTitle = `${article.seo?.title ?? title}${SEO_TITLE_SUFFIX}`;

  return (
    <>
      <div className="bg-gray-lighter py-1">
        {error && (
          <LayoutBlog className="m-section-hero container lg:max-w-screen-md">
            <h1 className="text-h1-display">Sorry</h1>

            <p>We seem to be experiencing issues with our blog. Please try again later.</p>
          </LayoutBlog>
        )}
      </div>
      <LayoutBlog>
        <Head>
          <title>{pageTitle}</title>
          <meta
            name="description"
            content={article.seo?.description && article.seo.description.length ? article.seo.description : excerpt(article)}
          />

          <meta property="og:url" content={`https://menguin.com/blog/${article.slug?.current}`} />
          <meta property="og:type" key="og:type" content="article" />
          <meta
            property="og:title"
            content={article.opengraph?.title && article.opengraph.title.length ? article.opengraph.title : pageTitle}
          />
          <meta
            property="og:description"
            content={
              article.opengraph?.description && article.opengraph.description.length ? article.opengraph.description : excerpt(article)
            }
          />
          <meta property="og:image" content={article.opengraph?.image?.asset.url ?? article.mainImage.asset.url} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta property="twitter:domain" content="menguin.com" />
          <meta property="twitter:url" content={`https://menguin.com/blog/${article.slug?.current}`} />
          <meta
            name="twitter:title"
            content={article.opengraph?.title && article.opengraph.title.length ? article.opengraph.title : pageTitle}
          />
          <meta
            name="twitter:description"
            content={
              article.opengraph?.description && article.opengraph.description.length ? article.opengraph.description : excerpt(article)
            }
          />
          <meta name="twitter:image" content={article.opengraph?.image?.asset.url ?? article.mainImage.asset.url} />
        </Head>
        <div dangerouslySetInnerHTML={{ __html: JsonLdScript(article, title) }} />

        <div className={`container lg:max-w-screen-md`}>
          <div className="row">
            <section
              className={`
                  col-span-12 bg-white shadow-3 md:col-span-8  
                  ${featuredProducts?.length ? '' : 'md:col-start-3'} 
                `}
            >
              <div className="p-32 sm:p-64">
                <span className="text-xs uppercase">{formatPublishedAt(article.publishedAt ?? '')}</span>
                <h1 className="text-h1-display">{title}</h1>
              </div>

              <BlogPicture
                alt={article.mainImage.alt ?? title}
                className="aspect-ratio-none"
                nativeWidth={article.mainImage.asset.metadata?.dimensions?.width!}
                ratio={`${article.mainImage.asset.metadata?.dimensions?.width} / ${article.mainImage.asset.metadata?.dimensions?.height}`}
                slot="postHero"
                url={article.mainImage.asset.url!}
              />

              <div>
                <PortableText
                  className="prose space-y-32 p-32 sm:p-64"
                  // Pass in block content straight from Sanity.io
                  content={article.bodyRaw as [object]}
                  serializers={{
                    picture: (props: { asset: SanityImageAsset & { _ref: string }; alt: string }) => {
                      return (
                        <BlogPicture
                          alt={props.alt}
                          nativeWidth={(parseAssetId(props.asset._ref) as SanityImageAssetIdParts).width}
                          ratio={`${(parseAssetId(props.asset._ref) as SanityImageAssetIdParts).width} / ${
                            (parseAssetId(props.asset._ref) as SanityImageAssetIdParts).height
                          }`}
                          slot="postContent"
                          url={getImageUrl(props.asset) ?? 'missing-image'}
                        />
                      );
                    },
                    youtube: (props: { url: string }) => {
                      return (
                        <GatedVideoEmbed>
                          <figure className="aspect-w-16 aspect-h-9">
                            <iframe
                              className=""
                              loading="lazy"
                              src={props.url}
                              allowFullScreen={true}
                              style={{ border: 0 }}
                              sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
                            />
                          </figure>
                        </GatedVideoEmbed>
                      );
                    },
                    button: (props: { href: string; style: string; text: string }) => (
                      <Link href={props.href} className={`btn ${buttonStyle(props.style as ButtonStyle | ButtonClass)}`}>
                        {props.text}
                      </Link>
                    ),
                    link: (props: { href: string; children: string[] }) => {
                      return (
                        <a href={props.href} rel="noreferrer" target="_blank">
                          {props.children[0]}
                        </a>
                      );
                    },
                  }}
                />
              </div>
            </section>

            {!!featuredProducts?.length && (
              <section className="col-span-12 mt-64 md:col-span-4 md:-mt-64 ">
                <h3 className="text-h3 mb-32">Featured Products</h3>
                <div className="grid grid-cols-2 gap-32 md:grid-cols-1">
                  {featuredProducts.map((product) => {
                    return (
                      <Card
                        key={product.id}
                        className="col-span-1"
                        trackerClass={`tracker-card-blog_${article.slug?.current}-${product.url_slug}-20211209-162845`}
                        ctaTitle="View Product"
                        href={getItemUrl(product)}
                        title={product.display_name ?? ''}
                        image={
                          <Picture
                            url={getProductImageUrl(product, 'PDP_Hero') as string}
                            className="p-4"
                            alt={product.display_name ?? 'Missing Alt Attribute'}
                            focalPointY={product.type === 'tuxedo' || product.type === 'suit' ? 0.0 : 0.5}
                          />
                        }
                      />
                    );
                  })}
                </div>
              </section>
            )}
          </div>

          <div className="col-span-12 col-start-1 mt-128">
            <Link
              href={PAGE_BLOG}
              className={`tracker-btn btn-default btn btn-blog_${article.slug?.current}-back_to_blog-20210615-143227 col-start-2`}
            >
              <IconArrowLeft />
              Back to Blog
            </Link>
          </div>
        </div>
      </LayoutBlog>
      <NextSteps
        ctaPageName="blog"
        salesCallout1={<SalesCalloutSwatches ctaPageName="blog" />}
        salesCallout2={<SalesCalloutInspiration ctaPageName="blog" />}
      />
    </>
  );
};

// This function gets called at build time
// { fallback: false } means other routes should 404.
export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const posts = await getAllBlogPosts();

    const paths = posts.allBlogPost.map((document: BlogPostFragment) => {
      return {
        params: {
          post: document.slug?.current,
        },
      };
    });

    return {
      paths,
      fallback: false,
    };
  } catch (error) {
    console.error('GetStaticPaths', error);
    return { paths: [], fallback: false };
  }
};

export const getStaticProps = withPageStaticProps(async ({ params }: GetStaticPropsContext) => {
  const { post } = params!;

  try {
    const response = await getBlogPost(post as string);

    return {
      props: {
        ...response,
        error: false,
      },
    };
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get blog post');
  }
});

export default Blog;
